import axiosClient from "../../../shared/lib/http/axiosClient";
import { Work, WorkAdd, WorkEdit, WorkSchema, WorkSearch, WorksPaginatedSchema, WorksResponse } from "@models/work";
import { buildSpatieSort } from "../utils/buildSpatieSort";
import { normalizeCustomDataFilter } from "../utils/normalizeCustomDataFilter";

type WorkInclude = "customData";

export type WorkSortField =
  | "id"
  | "work_code"
  | "name"
  | "price"
  | "hours"
  | "manufacturer"
  | "model"
  | "created_at"
  | "updated_at";

export type WorkSort = WorkSortField | `-${WorkSortField}`;

const WORK_SORT_FIELDS: WorkSortField[] = [
  "id",
  "work_code",
  "name",
  "price",
  "hours",
  "manufacturer",
  "model",
  "created_at",
  "updated_at",
];

const WORK_BROWSE_INCLUDES: WorkInclude[] = ["customData"];
const WORK_BROWSE_PER_PAGE = 10;

export interface WorkSearchParams {
  filter?: Partial<WorkSearch>;
  include?: WorkInclude[];
  sort?: WorkSort[];
  per_page?: number;
  cursor?: string;
}

function normalizeWorkSearchFilter(filter: WorkSearch): WorkSearch {
  const custom_data = normalizeCustomDataFilter(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;
  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}

function getSearchBody(params: WorkSearchParams) {
  const { filter: rawFilter, include, sort, per_page = WORK_BROWSE_PER_PAGE, cursor } = params;
  const filter = rawFilter ? normalizeWorkSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length && { include: include.join(",") }),
    ...(sort?.length && { sort: sort.join(",") }),
    ...(per_page && { per_page }),
    ...(cursor && { cursor }),
  };
}

export const buildWorkSort = (sortKey: string, order: "ASC" | "DESC"): WorkSort =>
  buildSpatieSort(sortKey, order, WORK_SORT_FIELDS, "id") as WorkSort;

export const searchWorks = async (
  params: WorkSearchParams = {},
  signal?: AbortSignal,
): Promise<WorksResponse> => {
  const response = await axiosClient.post(
    "/api/works/search",
    getSearchBody({
      include: WORK_BROWSE_INCLUDES,
      per_page: WORK_BROWSE_PER_PAGE,
      ...params,
    }),
    { signal },
  );

  return WorksPaginatedSchema.parse(response.data);
};

export const getWorkById = async (id: string, signal?: AbortSignal): Promise<Work> => {

  const response = await axiosClient.get(
    `/api/works/${id}`,
    { signal },
  );

  return response.data;
};


export const getNextWorkNumber = async (): Promise<number> => {

  const response = await axiosClient.get(
    "/api/works/next-number",
  );

  return response.data.next_work_index as number;
};


export const addWork = async (workToAdd: WorkAdd): Promise<Work> => {

  const response = await axiosClient.post(
    "/api/works",
    workToAdd,
  );

  return response.data;
};


export const deleteWork = async (workId: number): Promise<void> => {
  await axiosClient.delete(
    `/api/works/${workId}`,
  );
};


export const saveWork = async (workId: number, workToSave: WorkEdit): Promise<Work> => {

  const response = await axiosClient.patch(
    `/api/works/${workId}`,
    workToSave,
  );

  return WorkSchema.parse(response.data);
};
